/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */
'use client';

import React from 'react';
import Image from 'next/image';
import Alert from 'public/shared-icons/Alert.svg';
import Close from 'public/shared-icons/Close.svg';

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';

import { CustomButton } from '../common';

interface ConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string | undefined;
  description?: React.ReactNode;
  onConfirm: () => Promise<void>;
  isLoading?: boolean;
  confirmButtonText?: string;
  cancelButtonText?: string;
  children?: React.ReactNode;
  confirmButtonDisabled?: boolean;
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  isOpen,
  onClose,
  title,
  description,
  onConfirm,
  isLoading = false,
  confirmButtonText = 'Delete',
  cancelButtonText = 'Cancel',
  children,
  confirmButtonDisabled = false,
}) => {
  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent
        className="z-[80] flex w-[480px] flex-col justify-between gap-0 border-link-active bg-gradient-sky-dark p-8 "
        aria-describedby={undefined}
      >
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-8 top-8 h-6 w-6 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
          onClick={onClose}
        >
          <Image src={Close} alt="close icon" className="text-icon-fill" />
        </Button>

        <AlertDialogTitle className="flex items-center gap-2 border-b border-line-primary pb-5 text-2xl font-normal">
          <Image src={Alert} alt="alert icon" className="h-8 w-8" />
          {title}
        </AlertDialogTitle>

        <div className="flex flex-col gap-6 py-4">
          <div className="text-body font-normal leading-[19.6px] text-body-primary">
            {description}
          </div>
          {children && <div>{children}</div>}
        </div>

        <AlertDialogFooter className="gap-3 border-t border-line-primary pt-5 sm:justify-start">
          <CustomButton
            type="submit"
            customSize="regular"
            customVariant="primary"
            onClick={onConfirm}
            disabled={isLoading || confirmButtonDisabled}
          >
            {confirmButtonText}
          </CustomButton>

          <CustomButton
            customSize="regular"
            customVariant="secondary"
            onClick={onClose}
          >
            {cancelButtonText}
          </CustomButton>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export { ConfirmationDialog };
